import type { PerformedSet, ReadinessCheckin, TrainingDecision } from '@cadencia/contracts';
import {
  adjustVolume,
  assessDeload,
  haltingCodes,
  bandFor,
  countVolume,
  detectStall,
  findExercise,
  isReadaptationPhase,
  keptWeekRecord,
  performanceTrend,
  proposalFromDeload,
  proposalFromReorganization,
  proposalFromStall,
  proposalFromVolume,
  recoveryState,
  reorganizeWeek,
  REVIEW_WINDOWS,
  settleVolumeAdjustment,
  volumeReport,
  type AppliedVolumeChange,
  type CountableSet,
  type DeloadAssessment,
  type DecisionRecord,
  type ExerciseHistory,
  type Muscle,
  type MuscleVolumeReport,
  type PerformanceTrend,
  type ReorganizeDecision,
  type RepTarget,
  type StallAssessment,
  type VolumeAdjustment,
} from '@cadencia/domain';
import {
  addDays,
  diaLocalDe,
  parseLocalDate,
  startOfWeek,
  todayLocal,
} from '../../lib/dates.js';
import { logger } from '../../lib/logger.js';
import { db } from '../db.js';
import { listRecentDecisions, saveDecisions } from '../repositories/decisionsRepo.js';
import { currentRedFlags } from './safetyService.js';
import { applyDecisions, type ApplyOutcome } from './applyDecisionsService.js';
import {
  getActiveBlock,
  listSessionsOfDateRange,
  markMissedSessions,
  weekSessions,
} from '../repositories/trainingRepo.js';
import { refreshProgressionState } from '../repositories/progressionRepo.js';
import { getExerciseHistory } from '../repositories/workoutRepo.js';
import type { WriteContext } from '../writes.js';
import { nowIso } from '../ids.js';

/**
 * Revision semanal: mira lo entrenado, aplica los motores de la revision y deja
 * las propuestas registradas.
 *
 * Es la pieza que conecta el dominio con la aplicacion. Hasta ahora los motores
 * calculaban correctamente y nadie los llamaba.
 *
 * Todo el analisis sale de datos LOCALES: funciona sin conexion, igual que el
 * resto de la aplicacion.
 */

/** Semanas de historial que se miran. La ventana es del dominio (E4). */
const SEMANAS_DE_HISTORIAL = REVIEW_WINDOWS.weeklyHistoryWeeks;

export interface WeeklyReviewResult {
  weekStart: string;
  report: MuscleVolumeReport[];
  deload: DeloadAssessment;
  reorganization: ReorganizeDecision;
  /** Estancamientos detectados, por ejercicio. */
  stalls: EstancamientoDetectado[];
  /** Lo que se ha llevado al plan en esta pasada. */
  applied: ApplyOutcome;
  /** Propuestas nuevas guardadas en esta pasada. */
  created: TrainingDecision[];
  /**
   * La semana se reviso y el plan sigue igual, registrado como decision.
   *
   * Va aparte de `created` a proposito: quien pinta «qué ha cambiado esta
   * semana» —el check-in semanal— recorre `created`, y «nada» no es un cambio.
   */
  kept: TrainingDecision | null;
  /** Lo que no se propuso y por que. */
  skipped: string[];
}

// ---------------------------------------------------------------------------
// Recogida de datos
// ---------------------------------------------------------------------------

interface SemanaEntrenada {
  weekStart: string;
  sets: PerformedSet[];
}

function enSemana(fechaIso: string, weekStart: string): boolean {
  const dia = diaLocalDe(fechaIso);
  return dia >= weekStart && dia < addDays(weekStart, 7);
}

/**
 * Las series que `partirEnSemanas` puede necesitar, y ni una mas.
 *
 * Antes traia TODAS las del usuario y se quedaba con las de las cuatro semanas
 * (PERF-001). El limite no es una eleccion: es exactamente el principio de la
 * primera semana que se reparte, asi que el conjunto util no cambia.
 */
async function seriesDelUsuario(userId: string, desdeSemana: string): Promise<PerformedSet[]> {
  const filas = await db.performedSets
    .where('performedAt')
    .aboveOrEqual(parseLocalDate(desdeSemana).toISOString())
    .toArray();
  return filas.filter((fila) => fila.userId === userId && fila.deletedAt === null);
}

function partirEnSemanas(sets: readonly PerformedSet[], ultimaSemana: string): SemanaEntrenada[] {
  const semanas: SemanaEntrenada[] = [];
  for (let atras = SEMANAS_DE_HISTORIAL - 1; atras >= 0; atras -= 1) {
    const weekStart = addDays(ultimaSemana, -7 * atras);
    semanas.push({
      weekStart,
      sets: sets.filter((set) => enSemana(set.performedAt, weekStart)),
    });
  }
  return semanas;
}

function contables(sets: readonly PerformedSet[]): CountableSet[] {
  return sets.map((set) => ({ exerciseSlug: set.exerciseSlug, setType: set.setType }));
}

/** e1RM medio de un musculo en una semana, mirando solo donde es primario. */
function e1rmDeMusculo(sets: readonly PerformedSet[], muscle: Muscle): number | null {
  const relevantes = sets.filter((set) => {
    const ejercicio = findExercise(set.exerciseSlug);
    return ejercicio?.primaryMuscles.includes(muscle) ?? false;
  });
  if (relevantes.length === 0) return null;
  return relevantes.reduce((suma, set) => suma + set.e1rmKg, 0) / relevantes.length;
}

function tendenciaDeMusculo(semanas: readonly SemanaEntrenada[], muscle: Muscle): PerformanceTrend {
  const serie = semanas
    .map((semana) => e1rmDeMusculo(semana.sets, muscle))
    .filter((valor): valor is number => valor !== null);
  return performanceTrend(serie);
}

/** Semanas seguidas, contando hacia atras, con la misma tendencia que la ultima. */
function semanasEnTendencia(semanas: readonly SemanaEntrenada[], muscle: Muscle): number {
  const actual = tendenciaDeMusculo(semanas, muscle);
  if (actual === 'unknown') return 0;

  let seguidas = 0;
  for (let corte = semanas.length; corte >= 2; corte -= 1) {
    if (tendenciaDeMusculo(semanas.slice(0, corte), muscle) !== actual) break;
    seguidas += 1;
  }
  return seguidas;
}

/**
 * Caida de rendimiento dentro de la sesion: cuanto baja el e1RM de la primera
 * a la ultima serie de un mismo ejercicio.
 *
 * Se calcula en lugar de darlo por cero. Un cero fingido haria que la
 * recuperacion pareciera mejor de lo que es, y la consecuencia no seria
 * neutra: habilitaria subidas de volumen que los datos no respaldan.
 */
function caidaIntraSesion(sets: readonly PerformedSet[]): number {
  const porEjercicio = new Map<string, PerformedSet[]>();
  for (const set of sets) {
    const clave = `${set.workoutLogId}|${set.exerciseSlug}`;
    porEjercicio.set(clave, [...(porEjercicio.get(clave) ?? []), set]);
  }

  const caidas: number[] = [];
  for (const grupo of porEjercicio.values()) {
    if (grupo.length < 3) continue;
    const ordenado = [...grupo].sort((a, b) => a.performedAt.localeCompare(b.performedAt));
    const primero = ordenado[0]?.e1rmKg ?? 0;
    const ultimo = ordenado[ordenado.length - 1]?.e1rmKg ?? 0;
    if (primero > 0) caidas.push(Math.max(0, (primero - ultimo) / primero));
  }

  if (caidas.length === 0) return 0;
  return caidas.reduce((suma, valor) => suma + valor, 0) / caidas.length;
}

async function checkinsRecientes(userId: string, desde: string): Promise<ReadinessCheckin[]> {
  const filas = await db.readinessCheckins.where('userId').equals(userId).toArray();
  return filas
    .filter((fila) => fila.deletedAt === null && fila.forDate >= desde)
    .sort((a, b) => b.forDate.localeCompare(a.forDate));
}

/** Check-ins seguidos con agujetas >= 4 en la MISMA region. */
function rachaDeAgujetas(checkins: readonly ReadinessCheckin[]): number {
  const porRegion = new Map<string, number>();
  let maxima = 0;

  for (const checkin of checkins) {
    const severas = new Set(
      (checkin.sorenessAreas as { region: string; intensity: number }[])
        .filter((area) => area.intensity >= 4)
        .map((area) => area.region),
    );
    for (const region of porRegion.keys()) {
      if (!severas.has(region)) porRegion.set(region, 0);
    }
    for (const region of severas) {
      const seguidas = (porRegion.get(region) ?? 0) + 1;
      porRegion.set(region, seguidas);
      maxima = Math.max(maxima, seguidas);
    }
  }
  return maxima;
}

/**
 * Proporcion de series en las que se llego mas cerca del fallo de lo previsto.
 *
 * Se calcula, no se pasa a cero: son 20 de los 110 puntos del `deload_score`,
 * y darlos por cero apagaria una señal de fatiga entera sin que se notara.
 */
async function rirPorDebajoDelObjetivo(sets: readonly PerformedSet[]): Promise<number> {
  const conPrescripcion = sets.filter((set) => set.setPrescriptionId !== null);
  if (conPrescripcion.length === 0) return 0;

  const ids = [...new Set(conPrescripcion.map((set) => set.setPrescriptionId as string))];
  const prescripciones = await db.setPrescriptions.bulkGet(ids);

  const objetivo = new Map<string, number>();
  for (const prescripcion of prescripciones) {
    if (prescripcion) objetivo.set(prescripcion.id, prescripcion.targetRir);
  }

  let porDebajo = 0;
  let comparables = 0;
  for (const set of conPrescripcion) {
    const objetivoRir = objetivo.get(set.setPrescriptionId as string);
    if (objetivoRir === undefined) continue;
    comparables += 1;
    if (set.rir < objetivoRir) porDebajo += 1;
  }

  return comparables === 0 ? 0 : porDebajo / comparables;
}

async function sesionesPerdidas(userId: string, weekStart: string, hoy: string): Promise<number> {
  const filas = await db.trainingSessions.where('userId').equals(userId).toArray();
  return filas.filter(
    (fila) =>
      fila.deletedAt === null &&
      fila.scheduledDate >= weekStart &&
      fila.scheduledDate < addDays(weekStart, 7) &&
      fila.scheduledDate < hoy &&
      fila.status !== 'completed',
  ).length;
}

// ---------------------------------------------------------------------------
// Sesiones de la semana, para la reorganizacion (§8)
// ---------------------------------------------------------------------------

/** Sesiones perdidas en las SEMANAS ANTERIORES, para distinguir patron de imprevisto. */
async function perdidasAnteriores(
  userId: string,
  weekStart: string,
  semanas: number,
): Promise<number> {
  const filas = await listSessionsOfDateRange(userId, addDays(weekStart, -7 * semanas), weekStart);
  return filas.filter((fila) => fila.status === 'missed' || fila.status === 'skipped').length;
}

// ---------------------------------------------------------------------------
// Estancamiento por ejercicio (§9)
// ---------------------------------------------------------------------------

/**
 * Objetivo con el que se prescribio por ultima vez ese ejercicio.
 *
 * Se busca en las prescripciones y no se deduce del bloque: dos ejercicios de
 * la misma sesion llevan objetivos distintos segun su rol, y usar el del
 * principal para un accesorio daria por estancado lo que solo esta prescrito
 * mas suave.
 */
async function objetivoDe(sets: readonly PerformedSet[]): Promise<RepTarget | undefined> {
  const conPrescripcion = [...sets]
    .filter((set) => set.setPrescriptionId !== null)
    .sort((a, b) => b.performedAt.localeCompare(a.performedAt));
  if (conPrescripcion.length === 0) return undefined;

  const prescripcion = await db.setPrescriptions.get(
    conPrescripcion[0]?.setPrescriptionId as string,
  );
  if (!prescripcion || prescripcion.deletedAt !== null) return undefined;

  return {
    repsMin: prescripcion.targetRepsMin,
    repsMax: prescripcion.targetRepsMax,
    rir: prescripcion.targetRir,
  };
}

interface EstancamientoDetectado {
  exerciseSlug: string;
  /** Hace falta para aplicar: el estado de progresion es por maquina (ADR-014). */
  equipmentId: string | null;
  assessment: StallAssessment;
}

/**
 * Evalua el estancamiento de cada ejercicio entrenado y persiste el contador.
 *
 * CADENCIA declarada: se evalua UNA VEZ POR SEMANA, no por sesion. El §9 cuenta
 * «3 sesiones consecutivas» para detectar, pero no dice cada cuanto sube el
 * contador. Semanal encaja con el resto de la revision y, con frecuencia 2 por
 * musculo, hace falta un mes para llegar al escalon de revision estructural. Por
 * sesion se llegaria en dos semanas, que para una decision capaz de cambiar la
 * fase del bloque es demasiado rapido.
 *
 * Dos pasadas, y la primera NO escribe. `detectStall` es puro y no toca el
 * contador; `refreshProgressionState` si lo incrementa, asi que llamarlo en las
 * dos pasadas subiria el contador dos veces por semana y adelantaria toda la
 * escalera al doble de velocidad.
 */
async function evaluarEstancamientos(
  ctx: WriteContext,
  sets: readonly PerformedSet[],
  isReadaptation: boolean,
  weekStart: string,
): Promise<EstancamientoDetectado[]> {
  // Una ventana por semana de historial. El corte es el LUNES SIGUIENTE a cada
  // semana, porque `stallCounterFromHistory` compara con `<`.
  const cortes = Array.from({ length: SEMANAS_DE_HISTORIAL }, (_, indice) =>
    addDays(weekStart, -7 * (SEMANAS_DE_HISTORIAL - 1 - indice) + 7),
  );

  const porEjercicio = new Map<string, PerformedSet[]>();
  for (const set of sets) {
    const clave = `${set.exerciseSlug}|${set.equipmentId ?? ''}`;
    porEjercicio.set(clave, [...(porEjercicio.get(clave) ?? []), set]);
  }

  interface Candidato {
    grupo: PerformedSet[];
    slug: string;
    equipmentId: string | null;
    equipmentKind: string;
    target: RepTarget;
    history: ExerciseHistory;
    parado: boolean;
    isCompound: boolean;
    pattern: string;
  }

  const candidatos: Candidato[] = [];

  for (const grupo of porEjercicio.values()) {
    const primero = grupo[0];
    if (!primero) continue;
    const catalogo = findExercise(primero.exerciseSlug);
    if (!catalogo) continue;

    const target = await objetivoDe(grupo);
    if (target === undefined) continue;

    const equipmentId = primero.equipmentId ?? null;
    const history = await getExerciseHistory(ctx.userId, primero.exerciseSlug, equipmentId);

    candidatos.push({
      grupo,
      slug: primero.exerciseSlug,
      equipmentId,
      equipmentKind: catalogo.equipmentKind,
      target,
      history,
      // Solo detecta: no escribe ni incrementa nada.
      parado: detectStall(history, target).stalled,
      isCompound: catalogo.isCompound,
      pattern: catalogo.pattern,
    });
  }

  const patronesParados = new Set(
    candidatos.filter((c) => c.parado && c.isCompound).map((c) => c.pattern),
  );

  // Segunda pasada: UNA sola escritura por ejercicio, ya con el cruce del §9.
  const detectados: EstancamientoDetectado[] = [];
  for (const candidato of candidatos) {
    const assessment = await refreshProgressionState(ctx, {
      exerciseSlug: candidato.slug,
      equipmentId: candidato.equipmentId,
      history: candidato.history,
      context: {
        exerciseSlug: candidato.slug,
        equipmentId: candidato.equipmentId,
        equipmentKind: candidato.equipmentKind,
        smallestIncrementKg: null,
        currentLoadKg: candidato.grupo[candidato.grupo.length - 1]?.loadKg ?? null,
        isReadaptation,
      },
      sessionId: null,
      target: candidato.target,
      stallCutoffs: cortes,
      simultaneousStalledPatterns: patronesParados.size,
      // Cadencia no prescribe nutricion (retirada el 21/09/2026), asi que no
      // sabe si hay deficit. `null` es «no se sabe», no «no lo hay»: el cruce
      // del §9 queda inactivo en vez de fingido, y es una decision, no una
      // espera. `recovery_context = 'reduced'` no lo sustituye: mezcla deficit,
      // sueño y estres, y el cruce pide deficit.
      aggressiveDeficit: null,
    });

    if (assessment?.stalled === true) {
      detectados.push({
        exerciseSlug: candidato.slug,
        equipmentId: candidato.equipmentId,
        assessment,
      });
    }
  }

  return detectados;
}

// ---------------------------------------------------------------------------
// Revision
// ---------------------------------------------------------------------------

export interface WeeklyReviewOptions {
  /** Lunes de la semana a revisar. Por defecto, la semana en curso. */
  weekStart?: string;
  /**
   * La semana revisada TERMINA con esta revision: se deja constancia de que el
   * plan sigue igual si nada lo cambia.
   *
   * Solo al cerrarla. El panel de propuestas revisa la semana en curso cada vez
   * que se abre, y un «sin cambios» del martes no dice nada del domingo.
   */
  recordKeptPlan?: boolean;
  now?: string;
  today?: string;
}

/**
 * Revision en curso, si la hay.
 *
 * La deduplicacion por semana y musculo lee las decisiones existentes y despues
 * escribe. Dos revisiones solapadas comprueban las dos antes de que ninguna
 * haya escrito, y las dos crean: aparece la misma propuesta por duplicado.
 *
 * Pasa de verdad y no es un caso raro: en desarrollo React ejecuta los efectos
 * dos veces, asi que abrir el panel lanza dos revisiones a la vez. Serializarlas
 * es mas simple y mas fiable que intentar que la comprobacion y la escritura
 * sean atomicas en IndexedDB.
 */
let enCurso: Promise<WeeklyReviewResult> | null = null;

export async function runWeeklyReview(
  ctx: WriteContext,
  options: WeeklyReviewOptions = {},
): Promise<WeeklyReviewResult> {
  // Quien llegue mientras otra revision corre recibe SU resultado, no uno nuevo.
  if (enCurso !== null) return enCurso;
  enCurso = revisarSemana(ctx, options).finally(() => {
    enCurso = null;
  });
  return enCurso;
}

async function revisarSemana(
  ctx: WriteContext,
  options: WeeklyReviewOptions,
): Promise<WeeklyReviewResult> {
  const hoy = options.today ?? todayLocal();
  const weekStart = options.weekStart ?? startOfWeek(hoy);
  const now = options.now ?? nowIso();

  const todas = await seriesDelUsuario(
    ctx.userId,
    addDays(weekStart, -7 * (SEMANAS_DE_HISTORIAL - 1)),
  );
  const semanas = partirEnSemanas(todas, weekStart);
  const estaSemana = semanas[semanas.length - 1]?.sets ?? [];

  const recuento = countVolume(contables(estaSemana));
  const report = volumeReport(recuento);

  if (recuento.unknownSlugs.length > 0) {
    // No se calla: un slug fuera del catalogo deja al musculo por debajo de su
    // banda y la respuesta del motor a eso es AÑADIR volumen.
    logger.warn('engine', 'REVIEW_UNKNOWN_SLUGS', 'Ejercicios fuera del catalogo', {
      slugs: recuento.unknownSlugs,
    });
  }

  const checkins = await checkinsRecientes(ctx.userId, addDays(weekStart, -7));
  const deLaSemana = checkins.filter((checkin) => enSemana(checkin.forDate, weekStart));
  const readinessMedio =
    deLaSemana.length > 0
      ? deLaSemana.reduce((suma, checkin) => suma + checkin.readinessScore, 0) / deLaSemana.length
      : 100; // sin check-ins no hay señal de fatiga, no se inventa una
  const suenoMedio =
    deLaSemana.length > 0
      ? deLaSemana.reduce((suma, checkin) => suma + checkin.sleepHours, 0) / deLaSemana.length
      : 8;
  const caida = caidaIntraSesion(estaSemana);

  // --- propuestas de volumen, musculo a musculo -----------------------------
  const primeraSemana = semanas[0]?.sets ?? [];
  const recuentoInicial = countVolume(contables(primeraSemana));

  const yaDecidido = await listRecentDecisions(ctx.userId, 200);
  const registradas = new Set(
    yaDecidido
      .filter((fila) => (fila.inputs as { weekStart?: string }).weekStart === weekStart)
      .map((fila) => {
        const entradas = fila.inputs as { muscle?: string; exerciseSlug?: string };
        // El estancamiento se distingue por EJERCICIO, no por musculo: dos
        // ejercicios del mismo musculo pueden estancarse por separado y cada
        // uno merece su tarjeta.
        return `${fila.decisionType}|${entradas.muscle ?? entradas.exerciseSlug ?? ''}`;
      }),
  );

  const propuestas: DecisionRecord[] = [];
  const skipped: string[] = [];
  const sinCambioDeVolumen: VolumeAdjustment[] = [];
  const ultimosCambios = ultimoCambioDeVolumenPorMusculo(yaDecidido, weekStart);

  for (const entrada of report) {
    if (entrada.sets === 0) continue; // no se opina de lo que no se ha entrenado

    const clave = `volume_change|${entrada.muscle}`;
    if (registradas.has(clave)) {
      // Ya se propuso esta semana. Volver a preguntar lo mismo tras un rechazo
      // es insistir, y a la segunda se aprueba sin leer.
      skipped.push(`${entrada.muscle}: ya decidido esta semana`);
      continue;
    }

    const bruto = adjustVolume({
      muscle: entrada.muscle,
      currentSets: entrada.sets,
      blockStartSets: recuentoInicial.perMuscle[entrada.muscle] ?? entrada.sets,
      band: bandFor(entrada.muscle),
      performance: tendenciaDeMusculo(semanas, entrada.muscle),
      weeksInTrend: semanasEnTendencia(semanas, entrada.muscle),
      recovery: recoveryState({
        sorenessAvg: mediaDeAgujetas(deLaSemana),
        intraSessionDropPct: caida,
        readinessScore: readinessMedio,
      }),
      sorenessAvg: mediaDeAgujetas(deLaSemana),
    });
    // Una subida no se propone hasta que la anterior se haya podido medir (E4).
    const ajuste = settleVolumeAdjustment(
      bruto,
      ultimosCambios.get(entrada.muscle) ?? null,
      weekStart,
    );

    const propuesta = proposalFromVolume(ajuste, now);
    if (propuesta === null) {
      sinCambioDeVolumen.push(ajuste);
      continue;
    }
    propuestas.push(conSemana(propuesta, weekStart, entrada.muscle));
  }

  // --- propuesta de descarga ------------------------------------------------
  const deload = assessDeload({
    e1rmDecliningPatterns: patronesEnCaida(semanas),
    rirBelowTargetRatio: await rirPorDebajoDelObjetivo(estaSemana),
    readiness7dAvg: readinessMedio,
    readinessLowDays: deLaSemana.filter((c) => c.readinessScore <= 50).length,
    sleepAvgHours: suenoMedio,
    sleepLowDays: deLaSemana.filter((c) => c.sleepHours < 6).length,
    sorenessStreakCheckins: rachaDeAgujetas(checkins),
    missedSessions: await sesionesPerdidas(ctx.userId, weekStart, hoy),
    // INACTIVA A PROPOSITO, y no «hasta la Fase 6»: el peso diario existe
    // desde el 01/09/2026. La señal mide una bajada NO PLANIFICADA, y desde que
    // Cadencia no programa dieta no conoce el plan: un atleta en deficit por su
    // cuenta sumaria diez puntos de fatiga por hacer lo que queria hacer. El §6
    // del encargo pide ademas que una variacion de peso no decida nada por si
    // sola. Ceros iguales, para que la resta sea cero.
    bodyweightDropPct: 0,
    plannedBodyweightDropPct: 0,
    // Con una bandera roja activa, `assessDeload` devuelve `halted`: ni
    // propone descarga ni deja que nada mas prescriba. Hasta ahora este
    // argumento no se pasaba nunca, asi que esa rama era inalcanzable
    // (deuda D-3).
    redFlags: haltingCodes(await currentRedFlags(ctx.userId)),
  });

  if (!registradas.has('deload|')) {
    const propuesta = proposalFromDeload(deload, now);
    if (propuesta !== null) propuestas.push(conSemana(propuesta, weekStart, null));
  } else {
    skipped.push('descarga: ya decidido esta semana');
  }

  // --- sesiones perdidas y reorganizacion (§8) ------------------------------
  // Marcar primero: `reorganizeWeek` ya trata como perdida una sesion
  // planificada con fecha pasada, pero dejar la fila en `planned` haria que
  // siguiera figurando como pendiente para siempre.
  await markMissedSessions(ctx, hoy);

  const bloque = await getActiveBlock(ctx.userId);
  const reorganization = reorganizeWeek({
    weekStart,
    today: hoy,
    sessions: await weekSessions(ctx.userId, weekStart),
    musclePriorities: bloque?.musclePriorities,
    recentMissedSessions: await perdidasAnteriores(
      ctx.userId,
      weekStart,
      SEMANAS_DE_HISTORIAL - 1,
    ),
  });

  if (registradas.has('week_reorganization|')) {
    skipped.push('reorganizacion: ya decidido esta semana');
  } else {
    const propuesta = proposalFromReorganization(reorganization, now);
    if (propuesta !== null) propuestas.push(conSemana(propuesta, weekStart, null));
  }

  // --- estancamiento por ejercicio (§9) -------------------------------------
  const stalls = await evaluarEstancamientos(
    ctx,
    estaSemana,
    bloque !== undefined && isReadaptationPhase(bloque.phase),
    weekStart,
  );

  for (const { exerciseSlug, equipmentId, assessment } of stalls) {
    if (registradas.has(`stall_response|${exerciseSlug}`)) {
      skipped.push(`${exerciseSlug}: estancamiento ya decidido esta semana`);
      continue;
    }
    const propuesta = proposalFromStall(
      { slug: exerciseSlug, equipmentId },
      assessment,
      now,
    );
    if (propuesta !== null) {
      propuestas.push({
        ...propuesta,
        inputs: { ...propuesta.inputs, weekStart },
      });
    }
  }

  const created = await saveDecisions(ctx, propuestas);

  // --- mantener el plan (E4) ------------------------------------------------
  // Solo si la semana termina, si ningun motor ha propuesto nada en ESTA pasada
  // y si no hay ya otra decision de esa semana: una propuesta del miercoles que
  // se rechazo sigue siendo lo que se decidio esa semana, y un «sin cambios»
  // encima la contradiria.
  let kept: TrainingDecision | null = null;
  const semanaYaDecidida = yaDecidido.some(
    (fila) => (fila.inputs as { weekStart?: string }).weekStart === weekStart,
  );
  if (options.recordKeptPlan === true && propuestas.length === 0 && !semanaYaDecidida) {
    const registro = keptWeekRecord(
      {
        weekStart,
        setsLogged: estaSemana.length,
        volume: sinCambioDeVolumen,
        deload,
        reorganization,
        stalledExercises: stalls.length,
      },
      now,
    );
    if (registro !== null) kept = (await saveDecisions(ctx, [registro]))[0] ?? null;
  }

  // Y ahora se APLICA. Hasta aqui la revision dejaba siete motores proponiendo
  // y el plan intacto. Los menores entran solos (§10); los relevantes esperan a
  // que el usuario diga que si, y los caducados no entran nunca.
  const applied = await applyDecisions(ctx, now);

  logger.info('engine', 'WEEKLY_REVIEW_DONE', 'Revision semanal completada', {
    weekStart,
    propuestas: created.length,
    deloadScore: deload.score,
    sesionesPerdidas: reorganization.missedSessionIds.length,
    estancamientos: stalls.length,
    aplicadas: applied.applied.length,
  });

  return { weekStart, report, deload, reorganization, stalls, applied, created, kept, skipped };
}

/**
 * El ultimo cambio de volumen LLEVADO AL PLAN de cada musculo, anterior a la
 * semana revisada.
 *
 * Solo los aplicados: una propuesta rechazada o caducada no cambio nada, y
 * hacer esperar al musculo por ella seria castigar al usuario por decir que no.
 */
function ultimoCambioDeVolumenPorMusculo(
  decisiones: readonly TrainingDecision[],
  weekStart: string,
): Map<Muscle, AppliedVolumeChange> {
  const ultimos = new Map<Muscle, AppliedVolumeChange>();
  for (const fila of decisiones) {
    if (fila.decisionType !== 'volume_change' || fila.appliedAt === null) continue;
    const entradas = fila.inputs as { muscle?: Muscle; weekStart?: string };
    if (entradas.muscle === undefined || entradas.weekStart === undefined) continue;
    if (entradas.weekStart >= weekStart) continue;
    const previa = ultimos.get(entradas.muscle);
    if (previa !== undefined && previa.weekStart >= entradas.weekStart) continue;
    const antes = typeof fila.previousValue === 'number' ? fila.previousValue : 0;
    const despues = typeof fila.newValue === 'number' ? fila.newValue : antes;
    ultimos.set(entradas.muscle, { weekStart: entradas.weekStart, deltaSets: despues - antes });
  }
  return ultimos;
}

/** Anota la semana en las entradas: es la clave que evita proponer dos veces. */
function conSemana(
  record: DecisionRecord,
  weekStart: string,
  muscle: Muscle | null,
): DecisionRecord {
  return {
    ...record,
    inputs: { ...record.inputs, weekStart, ...(muscle === null ? {} : { muscle }) },
  };
}

function mediaDeAgujetas(checkins: readonly ReadinessCheckin[]): number {
  if (checkins.length === 0) return 0;
  return checkins.reduce((suma, checkin) => suma + checkin.soreness, 0) / checkins.length;
}

/** Patrones de movimiento cuyo e1RM medio cae al menos un 2,5 % en la ventana. */
function patronesEnCaida(semanas: readonly SemanaEntrenada[]): number {
  const patrones = new Set<string>();
  for (const semana of semanas) {
    for (const set of semana.sets) {
      const ejercicio = findExercise(set.exerciseSlug);
      if (ejercicio?.isCompound === true) patrones.add(ejercicio.pattern);
    }
  }

  let enCaida = 0;
  for (const patron of patrones) {
    const serie = semanas
      .map((semana) => {
        const relevantes = semana.sets.filter(
          (set) => findExercise(set.exerciseSlug)?.pattern === patron,
        );
        if (relevantes.length === 0) return null;
        return relevantes.reduce((suma, set) => suma + set.e1rmKg, 0) / relevantes.length;
      })
      .filter((valor): valor is number => valor !== null);

    if (serie.length >= 2 && performanceTrend(serie, 0.025) === 'falling') enCaida += 1;
  }
  return enCaida;
}
